<script setup lang="ts">
import { mdiChevronUp, mdiClockOutline } from "@mdi/js";
import groupBy from "lodash/groupBy";
import keyBy from "lodash/keyBy";
import ResultCardTitle from "~/components/layout/Search/ResultCardTitle.vue";
import SearchEventPrice from "~/components/layout/Search/SearchEventPrice.vue";
import LiveIndicator from "~/components/layout/Sport/LiveIndicator.vue";
import { IMarketHeader } from "~/types/config/marketHeader";
import { IEventDetails, IntDictionary } from "~/types/event/events";
import { IEvent } from "~/types/sports/event";
import { IMarket } from "~/types/sports/market";
import { IOutcome } from "~/types/sports/outcome";
import { IPrice } from "~/types/sports/price";
import sportUrlBuilder from "~/utils/sport/sportUrlBuilder";
import t from "~/utils/translate";

interface Props {
	initialState?: boolean;
	event: IEvent | undefined;
	markets: Map<number, IMarket>;
	outcomes: Map<number, IOutcome>;
	prices: Map<number, IPrice>;
	homePage?: boolean;
	marketHeaders?: { sport: string; sportMarkets: IMarketHeader[] }[];
}

const props = defineProps<Props>();
const active = ref(props.initialState ?? true);

const getMarketHeaders = (market: IMarket) => {
	const sport = props.marketHeaders?.find((x) => x.sport === props.event?.sportId);
	if (sport) {
		return sport.sportMarkets.find((x) => x.market === market.name);
	}
	return undefined;
};

const eventDetails = computed(() => {
	if (!props.event) {
		return;
	}
	const marketsArray = [...props.markets.values()].filter((market) => market.eventId === props.event?.eventId);

	let outcomes: IOutcome[] | undefined;
	try {
		const groupedOutcomes = groupBy(
			[...props.outcomes.values()].filter((outcome) => outcome.eventId === props.event?.eventId),
			"marketId",
		);
		if (groupedOutcomes[marketsArray[0].marketId][0].handicap) {
			outcomes = groupedOutcomes[marketsArray[0].marketId].sort(
				(a, b) => Math.abs(a.handicap) - Math.abs(b.handicap) || Number(a.originalMarketId) - Number(b.originalMarketId) || a.index - b.index,
			);
		} else {
			outcomes = groupedOutcomes[marketsArray[0].marketId].sort((a, b) => a.index - b.index) ?? null;
		}
	} catch (e) {
		outcomes = undefined;
	}
	const outcomeIds = outcomes?.map((o) => o.outcomeId) ?? [];

	const pricesArray = keyBy(
		[...props.prices.values()].filter((price) => outcomeIds.includes(price.outcomeId)),
		"outcomeId",
	);

	return {
		...props.event,
		scores: [],
		markets: marketsArray,
		outcomes,
		prices: pricesArray,
	} as IEventDetails;
});

const gridColsSize = (outComes: IntDictionary<IOutcome> | null | undefined) => {
	if (!outComes) return;
	let columns = Object.keys(outComes).length;
	if (Object.keys(outComes).length === 3) {
		columns = 3;
	} else if (Object.keys(outComes).length > 3) {
		columns = 2;
	}

	return `grid-template-columns: repeat(${columns}, minmax(0, 1fr))`;
};

const isDisabled = computed(() => {
	return (
		!eventDetails.value?.markets[0] ||
		!eventDetails.value?.outcomes ||
		eventDetails.value?.markets[0].isSuspended ||
		!eventDetails.value?.isActive ||
		!eventDetails.value?.markets[0].shouldDisplay
	);
});
</script>

<template>
	<UiAccordion @tab-open="active = true" @tab-close="active = false" :activeIndex="initialState ? 0 : 1" v-if="eventDetails?.markets[0]">
		<UiAccordionTab
			:class="['overflow-hidden rounded-xl', { 'dark:bg-dark-700': homePage }]"
			:headerWrapperClass="'w-full gap-1 rounded-t-lg dark:bg-dark-800 text-dark-300 dark:text-light-600 relative'"
		>
			<template #header>
				<div :class="['pr-[60px]', { 'dark:bg-dark-700': homePage }]">
					<ResultCardTitle :event="event"></ResultCardTitle>
					<div class="text-dark-300 dark:text-light-600 font-normal px-2 flex items-center relative flex-row whitespace-nowrap mb-2">
						<template v-if="event?.isLive">
							<LiveIndicator></LiveIndicator>
						</template>
						<template v-else>
							<div class="flex items-center">
								<UiIcon :path="mdiClockOutline" class="w-4 h-4 fill-dark-300 dark:fill-light-600 shrink-0 mr-1" />
								<UiDateTime dateFormat="dd MMM -" timeFormat=" HH:mm" time :date="event?.expectedStartEpoch || ''" />
							</div>
							<div class="overflow-hidden text-ellipsis"><span class="inline-block">&nbsp;|&nbsp;</span>{{ event?.league }}, {{ event?.region }}</div>
						</template>
					</div>
				</div>
			</template>
			<template #header-icon>
				<div class="absolute right-2 top-2">
					<UiButton
						size="custom"
						:icon="mdiChevronUp"
						:root-class="'ml-2 px-0.5 h-[28px] flex items-center justify-center '"
						:variant="'secondary'"
						:iconClass="['m-0 w-6 h-6 fill-[currentColor] transition-all', !active ? 'rotate-180' : '']"
					/>
				</div>
			</template>
			<template #default>
				<div class="grid gap-2 px-2 pb-2" :style="gridColsSize(eventDetails?.outcomes)" v-if="!isDisabled">
					<div
						v-if="marketHeaders"
						v-for="header in getMarketHeaders(eventDetails?.markets[0])?.marketOutcome"
						:key="header.outcomeKey"
						class="flex justify-center flex-1 text-xs font-bold leading-3 text-dark-700 dark:text-light-600"
					>
						{{ t(header.outcomeKey, { identifier: "market-headings." }) }}
					</div>
					<SearchEventPrice
						v-for="outcome in eventDetails?.outcomes"
						:key="outcome.outcomeId"
						:outcome="outcome"
						:event="eventDetails"
						:market="eventDetails?.markets[0]"
					></SearchEventPrice>
				</div>
				<div v-else-if="eventDetails?.markets[0]?.isSuspended" class="px-2 pb-2">
					<div
						:class="[
							'flex flex-1 h-8 items-center justify-center rounded-md border border-light-500 dark:border-dark-400 cursor-pointer p-2 bg-light-500 dark:bg-dark-400',
						]"
					>
						<span>{{ t("suspended") }}</span>
					</div>
				</div>
				<div v-else class="px-2 pb-2">
					<NuxtLink
						rel="ugc noindex nofollow"
						:href="
							sportUrlBuilder({
								fixtureType: eventDetails?.isLive ? 'live' : 'highlights',
								sport: eventDetails?.sportId,
								event: {
									awayTeam: eventDetails?.awayTeam ?? '',
									homeTeam: eventDetails?.homeTeam ?? '',
									eventId: eventDetails?.eventId ?? 0,
									leagueId: eventDetails?.leagueId ?? '',
									regionId: eventDetails?.regionId ?? '',
									expectedEndEpoch: eventDetails?.expectedEndEpoch ?? 0,
									expectedStartEpoch: eventDetails?.expectedStartEpoch ?? 0,
								},
							})
						"
						class="flex flex-1 h-8 items-center justify-center rounded-md border border-light-500 dark:border-dark-400 cursor-pointer p-2"
					>
						<span class="text-dark-700 dark:text-light-50">{{ t("more-bets") }}</span>
					</NuxtLink>
				</div>
			</template>
		</UiAccordionTab>
	</UiAccordion>
</template>

<style scoped lang="scss">
.wrapper {
	display: grid;
	grid-template-rows: 0fr;
	overflow: hidden;
	transition: grid-template-rows 0.3s;
}

.content {
	min-height: 0;
	transition: visibility 0.3s;
	visibility: hidden;
}

.wrapper.active {
	grid-template-rows: 1fr;
}

.wrapper.active .content {
	visibility: visible;
}
</style>
